/**
 * Maakt van een verse agendataak een huisbezoek dat aan één huishouden hangt.
 *
 * Er zijn drie plekken waar een huisbezoek zo ontstaat: een vervolgbezoek
 * vanuit een bestaand bezoek, een huishouden dat vanuit de lijst de agenda in
 * gesleept wordt, en de agenda van het huishouden zelf. Die deden alledrie
 * hetzelfde met de hand, en dat is precies waar ze uit elkaar gaan lopen.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * @typedef {object} AdressLabels
 * @property {string} [description] - 'Straat huisnummer, plaats'
 * @property {string} [adressLabel] - Alleen straat en huisnummer
 * @property {string} [adressName] - Naam van de bewoner
 *
 * @typedef {object} AdressVisitFallback
 * @property {string} [adressLabel] - Adres dat al op de bron stond
 * @property {string} [adressName] - Bewonersnaam die al op de bron stond
 * @property {string} [title] - Wat er als herkomst bij het bezoek hoort
 * @property {string} [description] - Adres als de aparte adresvelden ontbreken (de titel van het huishouden)
 *
 * @param {object} task - Verse taak uit `taskDTO.newDoc`
 * @param {object} options
 * @param {string} options.adressId - Het huishouden (`Adresses.ID`)
 * @param {AdressLabels} [options.labels] - Uitkomst van `getAdressLabels`
 * @param {AdressVisitFallback} [options.fallback] - Waarden van de bron, als de lees niets gaf
 * @param {{Title: string, Kind: string}} [options.defaultTitle] - Uitkomst van `defaultVisitTitle`; alleen gebruikt als de titel leeg is
 * @param {Array<string>} [options.teamMembers] - De medewerkers die aan het huishouden zijn toegewezen; alleen gebruikt als er nog niemand op de afspraak staat
 * @returns {object} Een kopie van `task`, klaar voor de taakbewerker
 */
const buildAdressVisitTask = (task, { adressId, labels, fallback, defaultTitle, teamMembers } = {}) => {
    if (!adressId) {
        throw new Error('buildAdressVisitTask: zonder huishouden is het geen huisbezoek')
    }

    const gelezen = labels || {}
    const terugval = fallback || {}

    return {
        ...task,
        Type: 'addressVisit',
        AdressID: adressId,
        // De andere contexten uitzetten en niet alleen overslaan: een taak die
        // uit een ander scherm komt draagt ze nog, en dan hangt het bezoek aan
        // twee dingen tegelijk en staat het in het rooster van iemand die er
        // niets mee te maken heeft.
        PersonaID: null,
        TeamMemberID: null,
        ActivityID: null,
        MeasureMomentID: null,
        OriginType: 'addressVisit',
        OriginID: adressId,
        OriginTitle: terugval.title || null,
        // Lege tekst en niet `undefined`: dat laatste landt als het WOORD
        // 'undefined' in de agendatitel zodra er iets aan geplakt wordt.
        // AI-CONTEXT: See docs/pitfalls.md#database-url-sentinel-string-persisted
        Description: gelezen.description || terugval.description || '',
        AdressLabel: gelezen.adressLabel || terugval.adressLabel || terugval.description || '',
        AdressName: gelezen.adressName || terugval.adressName || '',
        // Inplannen vult de afspraak in (verzoek BuurtKlusBedrijf): de
        // standaardsoort als titel en de toegewezen medewerker(s) — maar nooit
        // over iets heen dat de planner al gezet heeft.
        ...(defaultTitle && !String(task.Title || '').trim()
            ? { Title: defaultTitle.Title, Kind: defaultTitle.Kind }
            : {}),
        ...(Array.isArray(teamMembers) && teamMembers.length > 0 && !(Array.isArray(task.TeamMembers) && task.TeamMembers.length > 0)
            ? { TeamMembers: teamMembers }
            : {}),
    }
}

export default buildAdressVisitTask
